import { DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import {
  DEFAULT_TIMES,
  PERMITTED_VIEWS,
  TYPE_BONUS,
} from 'src/app/core/helpers/global/global.constant';
import {
  BONUS_PROMOTIONAL_CODES_COLUMNS,
  BONUS_PROMOTIONAL_CODES_COLUMNS_EXPORT,
  REPORT_MODULES,
} from 'src/app/core/helpers/global/reports.constant';
import { formatNumberTwoDecimals } from 'src/app/core/helpers/global/two-decimals-global-tables.utils';
import { orderColumnsByName } from 'src/app/core/helpers/global/global.util';
import {
  BUTTON_ACTIONS,
  FORMAT_FOR_DATES_LONG,
  swalWithBootstrapButtons,
} from 'src/app/core/helpers/ui/ui.constant';
import { PromotionalCode } from 'src/app/core/interfaces/api/promotional-tools.interface';
import {
  BonusPromotionalCode,
  DownloadReportRequest,
} from 'src/app/core/interfaces/api/report.interface';
import { ModalWithAction } from 'src/app/core/interfaces/ui/bootstrap-modal.interface';
import { NgxDatatableConfig } from 'src/app/core/interfaces/ui/ngx-datatable.interface';
import { ButtonAction } from 'src/app/core/interfaces/ui/ui.interface';
import { PromotionalCodeService } from 'src/app/core/services/api/promotional-code.service';
import { ReportService } from 'src/app/core/services/api/report.service';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { FilterService } from 'src/app/core/services/ui/filter.service';
import { LanguageService } from 'src/app/core/services/ui/language.service';
import { NgxDatatableService } from 'src/app/core/services/ui/ngx-datatable.service';
import { BooleanToYesNoPipe } from 'src/app/shared/pipes/boolean-to-yes-no.pipe';
import Swal from 'sweetalert2';
import { BonusReportInfoModalComponent } from '../bonus-report-info-modal/bonus-report-info-modal.component';
import { DEFAULT_NGX_DATATABLE_PAGINATION } from 'src/app/core/helpers/ui/ngx-datatable.constant';
import { ReportDownloadService } from 'src/app/core/services/ui/report-download.service';

@Component({
  selector: 'report-reporting-table-bonus-promocional-code',
  templateUrl: './reporting-table-bonus-promocional-code.component.html',
  providers: [NgxDatatableService, DatePipe, BooleanToYesNoPipe],
})
export class ReportingTableBonusPromocionalCodeComponent implements OnInit, OnChanges {
  @Input() public presetFilter: Record<string, unknown> = {};
  @Input() public showToolbar: boolean = true;
  @Input() public columnOrder: string[] = [];
  @Input() public hiddenColumns: string[] = [];

  public REPORT_MODULES = REPORT_MODULES.BONUS;
  public currentFilters: any = {};
  public currentLimit: number = DEFAULT_NGX_DATATABLE_PAGINATION.LIMIT;
  public currentPage: number = DEFAULT_NGX_DATATABLE_PAGINATION.PAGE;
  public FORMAT_FOR_DATES_LONG = FORMAT_FOR_DATES_LONG;
  public BUTTON_ACTIONS = BUTTON_ACTIONS;
  public TYPE_BONUS = TYPE_BONUS;
  public readonly PERMITTED_VIEWS = PERMITTED_VIEWS;

  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  public config$: Observable<Partial<NgxDatatableConfig>> = of({});
  public data$: Observable<any[]> = of([]);

  @ViewChild('bonusClaimDate', { static: true })
  public bonusClaimDateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('bonusCreationDate', { static: true })
  public bonusCreationDateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('amount', { static: true })
  public amountTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('amountDeposited', { static: true })
  public amountDepositedTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('amountBonusGenerated', { static: true })
  public amountBonusGeneratedTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('amountBonusPaid', { static: true })
  public amountBonusPaidTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('expirationDate', { static: true })
  public expirationDateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('wasDeposit', { static: true })
  public wasDepositeTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('bonusActivationDate', { static: true })
  public bonusActivationDateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('bonusResultDate', { static: true })
  public bonusResultDateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('bonusUpdateDate', { static: true })
  public bonusUpdateDateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('bonusState', { static: true })
  public bonusStateTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('bonusResult', { static: true })
  public bonusResultTemplate?: TemplateRef<HTMLElement>;

  @ViewChild('actions', { static: true })
  public actionsTemplate?: TemplateRef<HTMLElement>;

  constructor(
    public lang: LanguageService,
    private _reportService: ReportService,
    private _translateService: TranslateService,
    private _promotionalCodeService: PromotionalCodeService,
    private _bsModalService: BootstrapModalService<ModalWithAction<BonusPromotionalCode>>,
    private _datatable: NgxDatatableService<BonusPromotionalCode, object>,
    private _datePipe: DatePipe,
    private _booleanToYesNoPipe: BooleanToYesNoPipe,
    private _reportDownloadService: ReportDownloadService,
    private _filterService: FilterService<object>
  ) {}

  ngOnInit(): void {
    this.setTableConfig();
    this.config$ = this._datatable.config$;
    this.data$ = this._datatable.data$;
    this.applyPresetFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['presetFilter'] && !changes['presetFilter'].firstChange) this.applyPresetFilter();
  }

  private applyPresetFilter(): void {
    if (!Object.keys(this.presetFilter).length) return;
    this._filterService.updateFilterDataBehavior({ ...this.presetFilter });
  }

  private setTableConfig(): void {
    this._datatable.setDataSearchFunction((filters: object) => {
      this.currentFilters = filters;
      return this._reportService.getBonusPromotionalCodeReport(filters);
    });

    this._datatable.setTableConfig({
      sort: { prop: 'createdAt', dir: 'desc' },
      columns: this.getVisibleColumns(),
      exportDataFormatter: (bonusPromotionalCodes: BonusPromotionalCode[]) =>
        this.handleExport(bonusPromotionalCodes),
      exportColumnConfig: BONUS_PROMOTIONAL_CODES_COLUMNS_EXPORT,
      exportFileName: 'reports.bonus.bonusReport',
    });
  }

  private handleExport(bonusPromotionalCodes: BonusPromotionalCode[]) {
    return bonusPromotionalCodes.map((bonusPromo) => ({
      playerPromotionalCodeId: bonusPromo.playerPromotionalCodeId,
      externalPlayerId: bonusPromo.externalPlayerId,
      playerUsername: bonusPromo.playerUsername,
      agentExternalId: bonusPromo.agentExternalId,
      agency: bonusPromo.agentName,
      bonusId: bonusPromo.bonusId,
      typeBonus: bonusPromo.typeBonus,
      bonusName: bonusPromo.bonusName,
      bonusCode: bonusPromo.bonusCode,
      bonusCreationDate: this._datePipe.transform(bonusPromo.bonusCreationDate, 'dd/MM/yyyy'),
      activationDate: this._datePipe.transform(bonusPromo.activationDate, 'dd/MM/yyyy'),
      amount: formatNumberTwoDecimals(bonusPromo.amount),
      amountDeposited: formatNumberTwoDecimals(bonusPromo.amountDeposited),
      amountBonusPaid: formatNumberTwoDecimals(bonusPromo.amountBonusPaid),
      amountBonusGenerated: formatNumberTwoDecimals(bonusPromo.amountBonusGenerated),
      state: bonusPromo?.state?.[this.lang.code.value] || '',
      resultDate: this._datePipe.transform(bonusPromo.resultDate, 'dd/MM/yyyy'),
      result: bonusPromo?.result?.[this.lang.code.value] || '',
      expirationDate: this._datePipe.transform(bonusPromo.expirationDate, 'dd/MM/yyyy'),
      currecyCode: bonusPromo.currecyCode,
      wasDeposit: this._translateService.instant(
        this._booleanToYesNoPipe.transform(bonusPromo.wasDeposit)
      ),
      playerCategory: bonusPromo.playerCategory || '',
      product: bonusPromo.product || '',
      bonusClaimDate: this._datePipe.transform(bonusPromo.bonusClaimDate, 'dd/MM/yyyy'),
      updatedAt: this._datePipe.transform(bonusPromo.updatedAt, 'dd/MM/yyyy'),
      reasonCancellation: bonusPromo.reasonCancellation,
    }));
  }

  private getVisibleColumns(): Array<{ name?: string }> {
    const columns = (this.getColumnsConfig() as Array<{ name?: string }>).filter(
      (column) => !this.hiddenColumns.includes(column.name ?? '')
    );
    return this.columnOrder.length ? orderColumnsByName(columns, this.columnOrder) : columns;
  }

  private getColumnsConfig(): Array<object> {
    return [
      ...BONUS_PROMOTIONAL_CODES_COLUMNS,
      {
        name: 'reports.bonus.createdAt',
        cellTemplate: this.bonusCreationDateTemplate ?? undefined,
        width: 180,
      },
      {
        name: 'reports.bonus.acceptanceDate',
        cellTemplate: this.bonusActivationDateTemplate ?? undefined,
        width: 180,
      },
      {
        name: 'reports.bonus.bonusAmount',
        prop: 'amount',
        shouldSum: true,
        valueFormatter: formatNumberTwoDecimals,
        width: 150,
      },
      {
        name: 'words.amountDeposited',
        prop: 'amountDeposited',
        shouldSum: true,
        valueFormatter: formatNumberTwoDecimals,
        width: 150,
      },
      {
        name: 'reports.bonus.amountBonusPaid',
        prop: 'amountBonusPaid',
        shouldSum: true,
        valueFormatter: formatNumberTwoDecimals,
        width: 160,
      },
      {
        name: 'reports.bonus.amountBonusGenerated',
        prop: 'amountBonusGenerated',
        shouldSum: true,
        valueFormatter: formatNumberTwoDecimals,
        width: 160,
      },
      {
        name: 'reports.bonus.bonusStatus',
        cellTemplate: this.bonusStateTemplate ?? undefined,
        width: 160,
      },
      {
        name: 'reports.bonus.dateOfResult',
        cellTemplate: this.bonusResultDateTemplate ?? undefined,
        width: 180,
      },
      {
        name: 'reports.bonus.stateResult',
        cellTemplate: this.bonusResultTemplate ?? undefined,
        width: 160,
      },
      {
        name: 'reports.bonus.expirationDate',
        cellTemplate: this.expirationDateTemplate ?? undefined,
        width: 180,
      },
      {
        name: 'reports.transactions.currencyId',
        prop: 'currecyCode',
        width: 80,
      },
      {
        name: 'reports.bonus.wasDeposit',
        cellTemplate: this.wasDepositeTemplate ?? undefined,
        width: 100,
      },
      {
        name: 'reports.bonus.playerCategory',
        prop: 'playerCategory',
        width: 160,
      },
      {
        name: 'reports.bonus.product',
        prop: 'product',
        width: 150,
      },
      {
        name: 'reports.bonus.claimDate',
        cellTemplate: this.bonusClaimDateTemplate ?? undefined,
        width: 180,
      },
      {
        name: 'reports.bonus.updateBonusRegisterDate',
        cellTemplate: this.bonusUpdateDateTemplate ?? undefined,
        width: 180,
      },
      {
        name: 'reports.bonus.reasonCancellation',
        prop: 'reasonCancellation',
        width: 350,
      },
      {
        name: 'players.actions',
        width: 80,
        frozenRight: true,
        cellTemplate: this.actionsTemplate ?? undefined,
      },
    ];
  }

  public reloadDatatable(): void {
    this._datatable.reloadDatatable();
  }

  public onChangeLimit(limit: number): void {
    this._datatable.changeLimit(limit);
    this.currentLimit = limit;
  }

  public onChangePage(page: number): void {
    this._datatable.changePage(page);
    this.currentPage = page;
  }

  public openModal(buttonAction: ButtonAction, playerPromotionalCode?: PromotionalCode): void {
    if (buttonAction === BUTTON_ACTIONS.CANCEL) {
      this.confirmDelete(playerPromotionalCode);
    }
  }

  public openModalBonusReportInfo(
    buttonAction: ButtonAction,
    bonusReport?: BonusPromotionalCode
  ): void {
    this._bsModalService.openModal({
      component: BonusReportInfoModalComponent,
      data: { buttonAction, selectedRow: bonusReport },
      options: { size: 'xl' },
    });
  }

  private confirmDelete(playerPromotionalCode?: PromotionalCode): void {
    swalWithBootstrapButtons
      .fire({
        title: this._translateService.instant('players.blocked.table.confirmBlockedTitle'),
        text: this._translateService.instant('players.blocked.table.confirmBlockedText'),
        html: `
        <p>${this._translateService.instant('players.blocked.table.confirmBlockedText')}</p>
        <textarea id="cancelReason" class="swal2-textarea mx-0 px-0" placeholder="${this._translateService.instant(
          'reports.bonus.reasonPlaceHolder'
        )}" rows="10" cols="40"></textarea>
      `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        preConfirm: () => {
          const reason = (document.getElementById('cancelReason') as HTMLTextAreaElement).value;
          if (!reason.trim()) {
            Swal.showValidationMessage(
              this._translateService.instant('reports.bonus.reasonRequired')
            );
          }
          return reason;
        },
        confirmButtonText: this._translateService.instant('financial.bankEntities.confirm'),
        cancelButtonText: this._translateService.instant('financial.bankEntities.cancel'),
      })
      .then((result) => {
        if (result.isConfirmed && playerPromotionalCode && result.value) {
          const reason = result.value;
          this.cancelPromotionalCode(playerPromotionalCode, reason); // pásale la razón como argumento
        }
      });
  }

  cancelPromotionalCode(playerPromotionalCode: PromotionalCode, reason: string): void {
    if (!playerPromotionalCode) return;
    this._promotionalCodeService
      .cancelPromotionalCodeByPlayer(playerPromotionalCode._id, reason)
      .subscribe({
        next: (res) => {
          this.reloadDatatable();
          swalWithBootstrapButtons.fire(
            this._translateService.instant('reports.bonus.cancelSuccess'),
            this._translateService.instant('reports.bonus.cancelSuccessText'),
            'success'
          );
        },
        error: (err) => {
          swalWithBootstrapButtons.fire(
            this._translateService.instant('reports.bonus.cancelError'),
            this._translateService.instant(' reports.bonus.cancelErrorText'),
            'error'
          );
        },
      });
  }

  public onReportExportRequested(request: DownloadReportRequest): void {
    if (!this._reportDownloadService.validateExportRequest(request)) {
      return;
    }

    const filters = {
      ...this.currentFilters,
    };

    const downloadRequest = {
      ...request,
      filters,
    };

    this._reportDownloadService
      .executeReportDownload(downloadRequest, this.unsubscribe$)
      .subscribe();
  }
}
